import React, { useEffect, useState } from "react";
import axios from "axios";
import MapGL, { Marker, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl/dist/mapbox-gl";
import Card from "components/Card/Card.jsx";
import Button from "components/CustomButton/CustomButton.jsx";
import moment from "moment";
import Svg from "../../../components/Svg/Svg";
import {
  parseError,
  getAppointmentStatusName,
  useComponentSize,
  useWindowSize,
  getBoundsForPoints,
} from "api/common.js";
import "react-phone-number-input/style.css";
import "react-day-picker/lib/style.css";
import { Link } from "react-router-dom";
import Loader from "../../../views/Components/Loader/Loader.jsx";
import LocationMarker from "../../../assets/img/appointment-marker.png";
import CancelModal from "./CancelModal.jsx";
import RefundModal from "./RefundModal.jsx";
import RescheduleModal from "./RescheduleModal.jsx";
import ReassignModal from "./ReassignModal.jsx";
import FormPreviewModal from "./FormPreviewModal.jsx";
import NotesModal from "./NotesModal.jsx";
import { dateTimeFormat, dateFormat } from "api/common";

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

const AppointmentDetails = (props) => {
  const contentRef = React.useRef(null);
  const { width: contentWidth } = useComponentSize(contentRef);
  const { width: windowWidth } = useWindowSize();
  const [genders, setGenders] = useState([]);
  const [appointmentError, setAppointmentError] = useState(null);
  const [appointmentData, setAppointmentData] = useState(null);
  const [corporateData, setCorporateData] = useState(null);
  const [paymentsList, setPaymentsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewport, setViewport] = useState({
    latitude: 51.0338709,
    longitude: -114.0691901,
    zoom: 11,
    width: "100%",
    height: `400px`,
  });
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [formKey, setFormKey] = useState(null);
  const [formType, setFormType] = useState("requisition");
  const [appointmentItems, setAppointmentItems] = useState([]);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/account/genders`)
      .then((res) => {
        setGenders(res.data);
      })
      .catch((error) => {
        console.log(error);
      });
    const appointmentId = window.location.href.substring(window.location.href.lastIndexOf("/") + 1);
    if (appointmentId) {
      loadAppointment(appointmentId);
    }
  }, []);

  useEffect(() => {
    if (appointmentData && appointmentData.patient.corporateClientId) {
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/corporateclients/${encodeURIComponent(
            appointmentData.patient.corporateClientId
          )}`
        )
        .then((res) => {
          setCorporateData(res.data);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [appointmentData]);

  useEffect(() => {
    let vw = viewport;
    vw.width = `${contentWidth > 0 ? `${contentWidth}px` : "100%"}`;
    setViewport(vw);
  }, [contentWidth, windowWidth]);

  const loadAppointment = (appId) => {
    setAppointmentError(null);
    setAppointmentData(null);
    setLoading(true);

    axios
      .get(`${process.env.REACT_APP_API_URL}/appointments/${appId}`)
      .then((res) => {
        setLoading(false);
        setAppointmentData(res.data);
        getPayments(res.data.id);

        let vw = viewport;
        vw.latitude = res.data.location.latitude;
        vw.longitude = res.data.location.longitude;
        vw.width = `${contentWidth > 0 ? `${contentWidth}px` : "100%"}`;
        setViewport(vw);

        let points = [
          { latitude: res.data.location.latitude, longitude: res.data.location.longitude },
        ];

        try {
          let centre = getBoundsForPoints(points, 400);
          setViewport({
            latitude: centre.latitude,
            longitude: centre.longitude,
            zoom: (centre.zoom > 12 ? 12 : centre.zoom) || 12,
            width: "100%",
            height: `400px`,
          });
        } catch (mErr) {
          console.log(mErr);
        }

        if (res.data.status === "completed" || res.data.status === "ended") {
          getItems(res.data.id);
        }
      })
      .catch((err) => {
        setLoading(false);
        let error = parseError(err);
        setAppointmentError(error);
      });
  };

  const getItems = (appId) => {
    setAppointmentItems([]);

    axios
      .get(`${process.env.REACT_APP_API_URL}/appointments/${appId}/items`)
      .then((res) => {
        setAppointmentItems(res.data || []);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getPayments = (appointmentId) => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/appointments/${appointmentId}/payments`)
      .then((res) => {
        setPaymentsList(res.data || []);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const formatStatus = (status) => {
    var badgeColor = "primary";
    switch (status) {
      case "technician_assigned":
      case "technician_in_transit":
      case "technician_at_location":
      case "pending":
        badgeColor = "info";
        break;
      case "started":
      case "ended":
        badgeColor = "primary";
        break;
      case "completed":
        badgeColor = "success";
        break;
      case "cancelled":
        badgeColor = "warning";
        break;
    }

    return <div className={`badge ${badgeColor}`}>{getAppointmentStatusName(status)}</div>;
  };

  const formatDate = (inDate) => {
    if (!inDate) {
      return "--";
    }

    let dateString = moment(
      `${inDate.day}/${inDate.month}/${inDate.year} ${inDate.hour}:${inDate.minute}`,
      "D/M/YYYY H:mm"
    ).format(dateTimeFormat);

    return dateString;
  };

  const formatCancelReason = (reason) => {
    switch (reason) {
      case "requested_by_customer":
        return "Cancelled By Customer";
      case "cancelled_by_technician":
        return "Cancelled By Technician";
      case "due_failed_payment":
        return "Cancelled Due Failed Payment";
      case "cancelled_by_admin":
        return "Cancelled By Admin";
    }
  };

  const getNormalDate = (input) => {
    return moment(`${input.month}/${input.day}/${input.year}`, "M/D/YYYY");
  };

  const formatAddress = (address) => {
    if (!address) {
      return "";
    }
    return `${address.street}, ${address.city}, ${address.postalCode}, ${address.province}`;
  };

  const getEpochDate = (epoch) => {
    var date = new Date(0);
    date.setUTCSeconds(epoch);

    return date;
  };

  const formatPaymentStatus = (status) => {
    var badgeColor = "primary";
    var statusName = status;

    switch (status) {
      case "pending":
        badgeColor = "info";
        statusName = "Pending";
        break;
      case "authorized":
        badgeColor = "primary";
        statusName = "Pre-Authorized";
        break;
      case "failed":
        badgeColor = "error";
        statusName = "Failed";
        break;
      case "captured":
        badgeColor = "success";
        statusName = "Completed (Captured)";
        break;
      case "refunded":
        badgeColor = "warning";
        statusName = "Cancelled / Refunded";
        break;
    }

    return <div className={`badge ${badgeColor}`}>{statusName}</div>;
  };

  const formatPaymentType = (type) => {
    switch (type) {
      case "appointment":
        return "Appointment Payment";
      case "tip":
        return "Tip";
      case "cancellation_fee":
        return "Cancellation Fee";
    }
  };

  const getStripePaymentLink = (paymentId) => {
    let isProduction = process.env.NODE_ENV === "production";
    return `https://dashboard.stripe.com/${isProduction ? "" : "test/"}payments/${paymentId}`;
  };

  const getAvailableToRefund = (payList) => {
    let availableAmount = 0;

    payList
      .filter((s) => s.status === "captured")
      .forEach((p) => {
        availableAmount = availableAmount + (p.amount - p.refundedAmount);
      });

    return availableAmount;
  };

  const onFormView = (key, type) => {
    setFormType(type);
    setFormKey(key);
  };

  const getAppointmentItem = (list, item) => {
    if (list && list.length > 0 && item) {
      var aItem = list.find((s) => s.key === item.key);
      if (aItem) {
        let statusName =
          aItem.status === "dropped_off"
            ? "Dropped Off"
            : aItem.status === "completed"
            ? "Completed"
            : "Not Completed";

        return (
          <div className="flex items-center" style={{ marginTop: "6px", marginLeft: "24px" }}>
            <div className={`badge ${aItem.status === "dropped_off" ? "success" : "info"}`}>
              {statusName}
            </div>
            {aItem.dropOffLocation && (
              <div style={{ marginLeft: "4px" }}>{` • ${aItem.dropOffLocation.name} • ${moment(
                getEpochDate(aItem.droppedOffOn)
              ).format(dateTimeFormat)}`}</div>
            )}
          </div>
        );
      }
    }
    return null;
  };

  return (
    <div className="main-content create-appt flex gap-16 flex-col">
      <div className="flex items-cener">
        <Button
          simple
          icon
          style={{ padding: "0", marginBottom: "0px", marginRight: "auto" }}
          onClick={() => {
            props.history.go(-1);
          }}
        >
          <Svg name="chevron-left" className="w-6 h-6" />
          <span className="text-bold">Go Back</span>
        </Button>

        {appointmentData && (
          <div className="flex ml-auto gap-6" style={{ flexWrap: "wrap" }}>
            {(appointmentData.status === "pending" ||
              appointmentData.status === "technician_assigned" ||
              appointmentData.status === "technician_in_transit" ||
              appointmentData.status === "technician_at_location") && (
              <Button
                bsStyle="danger"
                fill
                disabled={loading}
                onClick={() => {
                  props.history.push(`/admin/appointment/edit?id=${appointmentData.id}`);
                }}
              >
                <Svg name="edit" className="w-4 h-4" />
                Edit
              </Button>
            )}
            {(appointmentData.status === "cancelled" ||
              appointmentData.status === "ended" ||
              appointmentData.status === "completed") && (
              <React.Fragment>
                <Button
                  disabled={
                    getAvailableToRefund(paymentsList) <= 0 || paymentsList.length === 0 || loading
                  }
                  bsStyle="danger"
                  fill
                  onClick={() => {
                    setRefundModalOpen(true);
                  }}
                >
                  <Svg name="undo" className="w-4 h-4" />
                  Refund
                </Button>

                <RefundModal
                  show={refundModalOpen}
                  appointmentId={appointmentData.id}
                  currency={appointmentData.cost.currency}
                  availableAmount={parseFloat(getAvailableToRefund(paymentsList))}
                  onHide={() => {
                    setRefundModalOpen(false);
                  }}
                  onConfirm={(list) => {
                    setPaymentsList(list);
                  }}
                />
              </React.Fragment>
            )}
            {appointmentData.status !== "cancelled" &&
              appointmentData.status !== "ended" &&
              appointmentData.status !== "completed" && (
                <React.Fragment>
                  <Button
                    bsStyle="danger"
                    fill
                    disabled={loading}
                    onClick={() => {
                      setReassignModalOpen(true);
                    }}
                  >
                    <Svg name="user-check" className="w-4 h-4" />
                    Reassign
                  </Button>

                  <ReassignModal
                    show={reassignModalOpen}
                    appointmentId={appointmentData.id}
                    appointmentDate={appointmentData.startTime}
                    onHide={() => {
                      setReassignModalOpen(false);
                    }}
                    onConfirm={(app) => {
                      setAppointmentData(app);
                    }}
                  />
                </React.Fragment>
              )}
            {appointmentData.type === "scheduled" &&
              (appointmentData.status === "technician_assigned" ||
                appointmentData.status === "pending") && (
                <React.Fragment>
                  <Button
                    bsStyle="danger"
                    fill
                    disabled={loading}
                    onClick={() => {
                      setRescheduleModalOpen(true);
                    }}
                  >
                    <Svg name="calendar" className="w-4 h-4" />
                    Reschedule
                  </Button>

                  <RescheduleModal
                    show={rescheduleModalOpen}
                    appointmentId={appointmentData.id}
                    appointment={appointmentData}
                    onHide={() => {
                      setRescheduleModalOpen(false);
                    }}
                    onConfirm={(app) => {
                      setAppointmentData(app);
                      getPayments(app.id);
                    }}
                  />
                </React.Fragment>
              )}
            {appointmentData.status !== "cancelled" &&
              appointmentData.status !== "ended" &&
              appointmentData.status !== "completed" && (
                <React.Fragment>
                  <Button
                    bsStyle="danger"
                    fill
                    disabled={loading}
                    onClick={() => {
                      setCancelModalOpen(true);
                    }}
                  >
                    <Svg name="close" className="w-4 h-4" />
                    Cancel
                  </Button>

                  <CancelModal
                    show={cancelModalOpen}
                    appointmentId={appointmentData.id}
                    externallyBilled={corporateData && corporateData.isBilledExternally}
                    onHide={() => {
                      setCancelModalOpen(false);
                    }}
                    onConfirm={(app) => {
                      setAppointmentData(app);
                      getPayments(app.id);
                    }}
                  />
                </React.Fragment>
              )}

            <Button
              bsStyle="danger"
              fill
              disabled={loading}
              onClick={() => {
                setNotesModalOpen(true);
              }}
            >
              <Svg name="file-text" className="w-4 h-4" />
              Notes
            </Button>

            <NotesModal
              show={notesModalOpen}
              appointmentId={appointmentData.id}
              notes={appointmentData.adminNotes}
              onHide={() => {
                setNotesModalOpen(false);
              }}
              onConfirm={(notes) => {
                let data = appointmentData;
                data.adminNotes = notes;
                setAppointmentData(data);
              }}
            />
          </div>
        )}
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center">
          <Loader />
          <p className="text-xs text-gray-400 mt-6" style={{ color: "var(--theme-color)" }}>
            Loading Appointment Details...
          </p>
        </div>
      )}

      {appointmentError && (
        <div
          className="error-alert"
          style={{ margin: "12px auto", padding: "0.6rem", maxWidth: "500px" }}
        >
          {appointmentError}
        </div>
      )}

      {appointmentData && (
        <div className="flex flex-col gap-16">
          <Card
            title="Appointment Summary"
            content={
              <div className="grid grid-2col gap-32">
                <div className="flex flex-col gap-4 items-start">
                  <div className="text-xxs text-semibold main-text" style={{ marginBottom: "6px" }}>
                    CODE
                  </div>
                  <div className="sub-text text-semibold text-xs">{appointmentData.id}</div>
                </div>

                <div className="flex flex-col gap-4 items-start">
                  <div className="text-xxs text-semibold main-text" style={{ marginBottom: "6px" }}>
                    STATUS
                  </div>
                  {formatStatus(appointmentData.status)}
                  {appointmentData.cancelReason !== "none" && (
                    <div className="sub-text text-xs">
                      {formatCancelReason(appointmentData.cancelReason)}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-4 items-start">
                  <div className="text-xxs text-semibold main-text" style={{ marginBottom: "6px" }}>
                    SCHEDULED TIME
                  </div>
                  {formatDate(appointmentData.startTime)}
                </div>

                <div className="flex flex-col gap-4 items-start">
                  <div className="text-xxs text-semibold main-text" style={{ marginBottom: "6px" }}>
                    APPOINTMENT TYPE
                  </div>
                  {appointmentData.type === "asap" ? "ASAP" : "Scheduled"}
                </div>

                <div className="flex flex-col gap-4">
                  <div className="text-xxs text-semibold main-text" style={{ marginBottom: "6px" }}>
                    BOOKED ON BEHALF OF
                  </div>
                  <div className="text-semibold text-lg">
                    <Link
                      className="text-semibold"
                      to={`/admin/users/${appointmentData.patient.id}`}
                      title="View Patient Details"
                    >
                      {appointmentData.patient.firstName} {appointmentData.patient.lastName}
                    </Link>
                    {appointmentData.patient.corporateClientId ? (
                      <React.Fragment>
                        {" • "}
                        <div className="badge success">Corporate Client</div>
                      </React.Fragment>
                    ) : null}
                  </div>
                  {corporateData && (
                    <React.Fragment>
                      <div className="sub-text text-xs">
                        {corporateData.name}
                        {corporateData.isBilledExternally
                          ? ` •  Billed Externally`
                          : "  •  Billed Internally"}
                      </div>
                    </React.Fragment>
                  )}
                  <div className="sub-text text-xs">{appointmentData.patient.email}</div>
                  <div className="sub-text text-xs">{appointmentData.patient.phoneNumber}</div>
                </div>

                <div className="flex flex-col gap-4 items-start">
                  <div className="text-xxs text-semibold main-text" style={{ marginBottom: "6px" }}>
                    ASSIGNED TECHNICIAN
                  </div>
                  {appointmentData.technician && (
                    <React.Fragment>
                      <div className="text-semibold text-lg">
                        <Link
                          className="text-semibold"
                          to={`/admin/lab-techs-details/${appointmentData.technician.id}`}
                          title="View Technician Details"
                        >
                          {appointmentData.technician.firstName}{" "}
                          {appointmentData.technician.lastName}
                        </Link>
                      </div>
                      <div className="sub-text text-xs">{appointmentData.technician.email}</div>
                      <div className="sub-text text-xs">
                        {appointmentData.technician.phoneNumber}
                      </div>
                    </React.Fragment>
                  )}
                  {!appointmentData.technician && (
                    <div className="sub-text text-xs">Not assigned</div>
                  )}
                </div>

                <div className="flex flex-col gap-4 items-start">
                  <div className="text-xxs text-semibold main-text" style={{ marginBottom: "6px" }}>
                    BOOKED AT
                  </div>
                  {formatDate(appointmentData.bookedAt)}
                </div>

                <div className="flex flex-col gap-4 items-start">
                  <div className="text-xxs text-semibold main-text" style={{ marginBottom: "6px" }}>
                    PATIENT NOTES
                  </div>
                  {appointmentData.patientNotes ? appointmentData.patientNotes : "--"}
                </div>

                <div className="flex flex-col gap-4 items-start">
                  <div className="text-xxs text-semibold main-text" style={{ marginBottom: "6px" }}>
                    TECHNICIAN NOTES
                  </div>
                  {appointmentData.technicianNotes ? appointmentData.technicianNotes : "--"}
                </div>

                <div className="flex flex-col gap-4 items-start">
                  <div className="text-xxs text-semibold main-text" style={{ marginBottom: "6px" }}>
                    ADMIN NOTES
                  </div>
                  {appointmentData.adminNotes ? appointmentData.adminNotes : "--"}
                </div>
              </div>
            }
          />

          <Card
            title="Cost & Payments"
            content={
              <div className="grid grid-2col gap-32">
                <div className="flex flex-col items-start">
                  <div
                    className="text-xxs text-semibold main-text"
                    style={{ marginBottom: "12px" }}
                  >
                    COST BREAKDOWN
                  </div>

                  <div className="flex flex-col w-full gap-10">
                    {corporateData && corporateData.isBilledExternally && (
                      <div className="flex flex-col gap-6">
                        <span className="main-text text-semibold text-lg">No Payment Required</span>
                        <span className="sub-text text-regular text-xs">Billed Externally</span>
                      </div>
                    )}

                    {(!corporateData || !corporateData.isBilledExternally) && (
                      <React.Fragment>
                        <div className="flex items-center">
                          <span>Subtotal</span>
                          <span className="text-semibold ml-auto">
                            ${" "}
                            {new Intl.NumberFormat("en-US", {
                              style: "decimal",
                              minimumFractionDigits: 2,
                              signDisplay: "never",
                            }).format(
                              appointmentData.cost.subtotal > 0
                                ? appointmentData.cost.subtotal / 100
                                : 0
                            )}
                          </span>
                        </div>

                        <div className="flex items-center">
                          <span>
                            GST ($
                            {new Intl.NumberFormat("en-US", {
                              style: "decimal",
                              minimumFractionDigits: 1,
                              signDisplay: "never",
                            }).format(appointmentData.cost.gstPercentage)}
                            %)
                          </span>

                          <span className="text-semibold ml-auto">
                            ${" "}
                            {new Intl.NumberFormat("en-US", {
                              style: "decimal",
                              minimumFractionDigits: 2,
                              signDisplay: "never",
                            }).format(
                              appointmentData.cost.gst > 0 ? appointmentData.cost.gst / 100 : 0
                            )}
                          </span>
                        </div>

                        {appointmentData.cost.otherTax > 0 && (
                          <div className="flex items-center">
                            <span>
                              Provincial Tax `($
                              {new Intl.NumberFormat("en-US", {
                                style: "decimal",
                                minimumFractionDigits: 1,
                                signDisplay: "never",
                              }).format(appointmentData.cost.otherTaxPercentage)}
                              %)`
                            </span>
                            <span className="text-semibold ml-auto">
                              ${" "}
                              {new Intl.NumberFormat("en-US", {
                                style: "decimal",
                                minimumFractionDigits: 2,
                                signDisplay: "never",
                              }).format(
                                appointmentData.cost.otherTax > 0
                                  ? appointmentData.cost.otherTax / 100
                                  : 0
                              )}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center">
                          <span>
                            Discount{" "}
                            {appointmentData.discountCodeUsed
                              ? `(${appointmentData.discountCodeUsed})`
                              : ""}
                          </span>
                          <span className="text-semibold ml-auto">
                            ${" "}
                            {new Intl.NumberFormat("en-US", {
                              style: "decimal",
                              minimumFractionDigits: 2,
                              signDisplay: "auto",
                            }).format(
                              appointmentData.cost.discount != 0
                                ? appointmentData.cost.discount / 100
                                : 0
                            )}
                          </span>
                        </div>

                        <div className="separator horizontal"></div>

                        <div className="flex items-center">
                          <span className="text-semibold">
                            Total ({appointmentData.cost.currency})
                          </span>
                          <span className="text-semibold ml-auto">
                            ${" "}
                            {new Intl.NumberFormat("en-US", {
                              style: "decimal",
                              minimumFractionDigits: 2,
                              signDisplay: "never",
                            }).format(
                              appointmentData.cost.total > 0 ? appointmentData.cost.total / 100 : 0
                            )}
                          </span>
                        </div>

                        <div className="separator horizontal"></div>

                        <div className="flex items-center">
                          <span>Technician's Commission</span>
                          <span className="ml-auto">
                            ${" "}
                            {new Intl.NumberFormat("en-US", {
                              style: "decimal",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                              signDisplay: "never",
                            }).format(
                              appointmentData.cost.technicianPortion > 0
                                ? appointmentData.cost.technicianPortion / 100
                                : 0
                            )}
                          </span>
                        </div>
                      </React.Fragment>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-6 items-start w-full">
                  <div
                    className="text-xxs text-semibold main-text"
                    style={{ marginBottom: "10px" }}
                  >
                    PAYMENTS
                  </div>

                  {paymentsList.map((payment, key) => (
                    <div
                      key={key}
                      style={{
                        padding: "8px 12px",
                        border: "1px solid var(--border-color)",
                        borderRadius: "6px",
                      }}
                      className="flex flex-col gap-12 w-full"
                    >
                      <div className="flex items-center gap-12">
                        <div className="text-bold text-normal" style={{ whiteSpace: "nowrap" }}>
                          ${" "}
                          {new Intl.NumberFormat("en-US", {
                            style: "decimal",
                            minimumFractionDigits: 2,
                            signDisplay: "never",
                          }).format(payment.amount > 0 ? payment.amount / 100 : 0)}
                        </div>

                        <div className="separator vertical"></div>

                        <div className="flex flex-col gap-4">
                          <div className="sub-text text-xs">
                            <span className="text-semibold main-text">
                              {formatPaymentType(payment.paymentType)}
                            </span>
                            {" • "}
                            {formatPaymentStatus(payment.status)}
                          </div>
                          <div className="sub-text text-xs">
                            {moment(
                              getEpochDate(payment.capturedOn || payment.authorizedOn)
                            ).format(dateTimeFormat)}
                            {payment.brand && (
                              <React.Fragment>
                                {" • "}
                                {`${payment.brand.toUpperCase()} **** ${payment.last4}`}
                              </React.Fragment>
                            )}
                          </div>
                          {payment.refundedAmount > 0 && (
                            <div className="flex items-center">
                              <div className="success-text text-xs">
                                Refunded: $
                                {new Intl.NumberFormat("en-US", {
                                  style: "decimal",
                                  minimumFractionDigits: 2,
                                  signDisplay: "never",
                                }).format(payment.refundedAmount / 100)}{" "}
                                on {moment(getEpochDate(payment.refundedOn)).format(dateTimeFormat)}
                              </div>
                            </div>
                          )}
                          {payment.error && <div className="theme-text">{payment.error}</div>}
                          <a
                            target="_blank"
                            className="flex items-center gap-4"
                            title="View Payment in Stripe"
                            href={getStripePaymentLink(payment.stripePaymentId)}
                          >
                            View Details in Stripe
                            <Svg className="w-4 h-4" name="external" />
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                  {paymentsList.length === 0 && <div className="sub-text text-lg">No charges</div>}
                </div>
              </div>
            }
          />

          <Card
            title="Patients"
            content={
              <div className="flex flex-col gap-16">
                {appointmentData.appointmentPatients.map((person, key) => (
                  <div
                    key={key}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid var(--border-color)",
                      borderRadius: "6px",
                    }}
                    className="flex flex-col gap-12"
                  >
                    <div className="flex items-start">
                      <div className="flex flex-col gap-4">
                        <div className="text-semibold text-lg">
                          {person.firstName} {person.lastName}
                        </div>
                        <div className="sub-text text-xs">
                          {person.email}
                          {" • "}
                          {person.phoneNumber}
                        </div>
                        <div className="sub-text text-xs">
                          {getNormalDate(person.dateOfBirth).format(dateFormat)}
                          {" • "}
                          {genders.find((g) => g.key == person.gender)
                            ? genders.find((g) => g.key == person.gender).value
                            : ""}
                        </div>
                      </div>
                    </div>

                    <div className="separator horizontal"></div>

                    <div className="flex flex-col gap-6">
                      <div className="sub-text text-bold text-xxs">SERVICES</div>
                      <div className="flex flex-col gap-10">
                        {person.appointmentItems.map((item) => (
                          <div key={item.key} className="flex flex-col items-start">
                            <div className="text-semibold flex items-center gap-6">
                              <span>{item.service.title}</span>
                              <span className="theme-text text-regular">
                                ($
                                {new Intl.NumberFormat("en-US", {
                                  style: "decimal",
                                  minimumFractionDigits: 2,
                                  signDisplay: "never",
                                }).format(item.service.price > 0 ? item.service.price / 100 : 0)}
                                )
                              </span>
                              {item.service.requisitionForm === true && (
                                <Button
                                  style={{
                                    fontSize: "12px",
                                    padding: "4px 10px",
                                    marginLeft: "12px",
                                  }}
                                  bsStyle="danger"
                                  fill
                                  onClick={() => {
                                    onFormView(item.key, "requisition");
                                  }}
                                >
                                  <Svg name="eye" className="w-4 h-4" />
                                  Requisition Form
                                </Button>
                              )}
                              {item.service.insuranceForm === true && (
                                <Button
                                  style={{
                                    fontSize: "12px",
                                    padding: "4px 10px",
                                    marginLeft: "12px",
                                  }}
                                  bsStyle="danger"
                                  fill
                                  onClick={() => {
                                    onFormView(item.key, "insurance");
                                  }}
                                >
                                  <Svg name="eye" className="w-4 h-4" />
                                  Insurance Form
                                </Button>
                              )}
                            </div>

                            {(appointmentData.status === "completed" ||
                              appointmentData.status === "ended") &&
                              appointmentItems &&
                              getAppointmentItem(appointmentItems, item)}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            }
          />

          <Card
            title="Address"
            content={
              <div className="flex flex-col gap-12">
                <div className="main-text text-xl text-semibold">
                  {formatAddress(appointmentData.address)}
                </div>
                <div className="separator horizontal"></div>
                <div
                  ref={contentRef}
                  style={{
                    position: "relative",
                    borderRadius: "6px",
                    overflow: "hidden",
                    border: "1px solid var(--border-color)",
                  }}
                >
                  <MapGL
                    scrollZoom={true}
                    maxPitch={85}
                    {...viewport}
                    onViewportChange={(viewport) => setViewport(viewport)}
                    mapStyle="mapbox://styles/mapbox/light-v10"
                    mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_API_KEY}
                  >
                    <NavigationControl
                      style={{
                        right: 12,
                        top: 12,
                      }}
                    />

                    <Marker
                      key={appointmentData.id}
                      longitude={appointmentData.location.longitude}
                      latitude={appointmentData.location.latitude}
                    >
                      <button
                        className="marker-btn"
                        onClick={(e) => {
                          e.preventDefault();
                        }}
                      >
                        <img src={LocationMarker} alt="Appointment Marker" />
                      </button>
                    </Marker>
                  </MapGL>
                </div>
              </div>
            }
          />
        </div>
      )}

      <FormPreviewModal
        show={formKey != null}
        type={formType}
        formKey={formKey}
        onHide={() => setFormKey(null)}
      />
    </div>
  );
};

export default AppointmentDetails;
